import { css } from '@emotion/react'
import React from 'react'

import { Icon } from '@/component/atoms/Icon'
import motionButton from '@/component/atoms/motionIcon'
import Name from '@/component/atoms/Name'
import Group from '@/component/molecules/Group'

import DualListItem from '../components/DualListItem'
import type { DualListItem as DualListItemType, RenderItemExtraParams } from '../types'

type Props<T extends DualListItemType> = {
  groupKey: string | number
  groupLabel: string
  items: T[]
  side: 'left' | 'right'
  isOpen: boolean
  onToggle: () => void
  onMoveAll: () => void
  disabledFilter?: (item: T) => boolean
  renderItemExtra?: (params: RenderItemExtraParams<T>) => React.ReactNode
  /** 아이템 커스텀 렌더링 (제공 시 DualListItem 대신 사용) */
  renderItem?: (item: T, onClick: () => void) => React.ReactNode
  onItemClick: (item: T) => void
}

const DualListGroup = <T extends DualListItemType>({
  groupKey,
  groupLabel,
  items,
  side,
  isOpen,
  onToggle,
  onMoveAll,
  disabledFilter,
  renderItemExtra,
  renderItem,
  onItemClick,
}: Props<T>) => {
  const iconName = side === 'left' ? 'add_circle' : 'remove_fill'
  const iconColor = side === 'left' ? 'var(--Colors-Sky-400)' : 'var(--Colors-Gray-900)'
  const enabledItems = disabledFilter ? items.filter((item) => !disabledFilter(item)) : items
  const isAllDisabled = enabledItems.length === 0

  return (
    <Group
      data-mathflat-unique-id={groupKey}
      isOpen={isOpen}
      GroupNode={
        <div css={S.groupHeader}>
          <button type="button" className="group-open-button" onClick={onToggle}>
            <motionButton.GroupArrow className="group-arrow" isOpen={isOpen} />
            <Name className="group-name" maxWidth={89}>
              {groupLabel}
            </Name>
            <span className="group-in-count">{items.length}명</span>
          </button>
          {!isAllDisabled && (
            <button
              type="button"
              className="icon-button"
              onClick={onMoveAll}
              aria-label={side === 'left' ? '전체 추가' : '전체 제거'}
            >
              <Icon name={iconName} size={20} color={iconColor} />
            </button>
          )}
        </div>
      }
    >
      {items.map((item) => {
        if (renderItem) {
          return (
            <React.Fragment key={item.id}>
              {renderItem(item, () => onItemClick(item))}
            </React.Fragment>
          )
        }
        const isDisabled = disabledFilter?.(item) ?? false
        return (
          <DualListItem
            key={item.id}
            item={item}
            side={side}
            disabled={isDisabled}
            grouped
            renderExtra={renderItemExtra}
            onClick={() => onItemClick(item)}
          />
        )
      })}
    </Group>
  )
}

export default DualListGroup

const S = {
  groupHeader: css`
    display: flex;
    align-items: center;
    height: 48px;
    padding: 0 24px 0 16px;
    border-bottom: 1px solid var(--Colors-Gray-300);
    background-color: var(--Colors-Gray-100);
    &:hover {
      background-color: var(--Colors-Gray-200);
    }
    .group-open-button {
      display: flex;
      align-items: center;
      flex: 1;
      height: 100%;
      cursor: pointer;
      &:disabled {
        cursor: default;
      }
    }
    .group-arrow {
      margin-right: 12px;
    }
    .group-name {
      margin-right: 12px;
    }
    .group-in-count {
      color: var(--Colors-Gray-600);
    }
    .icon-button {
      display: flex;
      align-items: center;
      height: 100%;
      cursor: pointer;
      &:disabled {
        cursor: not-allowed;
      }
    }
  `,
}
